import React, { useState, Children, memo } from 'react';
import { PenToolIcon, MailIcon, UserIcon, BriefcaseIcon, PenIcon, CalendarIcon, BookIcon } from 'lucide-react';
import { ConnectionPageLayout } from '../../components/connections/ConnectionPageLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
import { OpportunityItem } from '../../components/connections/OpportunityPreviewCard';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const ConnectWithAuthors = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Form State (kept from original)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    projectDescription: '',
    timeline: '',
    budget: '',
    authorPreferences: '',
    genreInterests: [] as string[],
    termsAgreed: false,
    submissionStatus: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleGenreChange = (genre: string) => {
    setFormData(prev => ({
      ...prev,
      genreInterests: prev.genreInterests.includes(genre) ? prev.genreInterests.filter(g => g !== genre) : [...prev.genreInterests, genre]
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const genres = ['Romance', 'Fantasy', 'Science Fiction', 'Mystery', 'Thriller', 'Horror', 'Historical Fiction', 'Young Adult', 'Literary Fiction', 'Non-Fiction', 'Biography', 'Self-Help', "Children's"];
  // Filters Configuration
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'relevance',
      label: 'Relevance'
    }, {
      value: 'name-asc',
      label: 'Name (A-Z)'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }, {
      value: 'reviews-desc',
      label: 'Most Reviews'
    }],
    value: 'relevance',
    onChange: () => {} // Handled in layout via state lift (mocked for now)
  }, {
    id: 'availability',
    title: 'Availability',
    type: 'checkbox',
    options: [{
      value: 'open',
      label: 'Open to Collabs'
    }, {
      value: 'limited',
      label: 'Limited Availability'
    }],
    value: [],
    onChange: () => {}
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: genres.map(g => ({
      value: g,
      label: g
    })),
    value: [],
    onChange: () => {}
  }, {
    id: 'collabType',
    title: 'Collaboration Type',
    type: 'checkbox',
    options: [{
      value: 'cowrite',
      label: 'Co-writing'
    }, {
      value: 'ghostwrite',
      label: 'Ghostwriting'
    }, {
      value: 'anthology',
      label: 'Anthology'
    }, {
      value: 'newsletter',
      label: 'Newsletter Swap'
    }],
    value: [],
    onChange: () => {}
  }];
  // Opportunities Data
  const opportunities: OpportunityItem[] = [{
    id: '1',
    title: 'Co-Author for Fantasy Series',
    poster: 'Marcus Chen',
    deadline: '2023-12-15',
    type: 'Co-authoring',
    description: 'Looking for a co-author to collaborate on a 3-book fantasy series with established world-building.'
  }, {
    id: '2',
    title: 'Short Story Collection - Horror',
    poster: 'Sarah Johnson',
    deadline: '2023-11-30',
    type: 'Anthology',
    description: 'Curating a collection of horror short stories. Looking for original, bone-chilling tales.'
  }, {
    id: '3',
    title: 'Ghostwriter for Memoir',
    poster: 'David Williams',
    deadline: '2023-12-30',
    type: 'Ghostwriting',
    description: 'Seeking a skilled ghostwriter to help craft a personal memoir about business success.'
  }];
  // Form Content JSX
  const formContent = <form onSubmit={handleSubmit} className="space-y-10" data-id="element-18284" data-mp="e68mfwhoscx7">
      {/* Contact Information */}
      <div data-id="element-18285" data-mp="e4dz6biy08py">
        <h2 className={`text-xl font-semibold mb-6 flex items-center pb-2 border-b ${dk.textPrimary} ${dk.border}`} data-id="element-18286" data-mp="3mlsckmn29f81">
          <MailIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-18287" data-mp="3hlw4ocb8t3n7" />
          Contact Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-18288" data-mp="1x44ydm0jjbso">
          <div data-id="element-18289" data-mp="21w53dsvgwuw4">
            <label htmlFor="name" className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-18290" data-mp="21wok569dtd8k">
              Your Name *
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${rt.inputFocus} ${dk.inputOnCard}`} placeholder="John Doe" required data-id="element-18291" data-mp="1s521wpymmdwb" />
          </div>
          <div data-id="element-18292" data-mp="2ava14wz7gk78">
            <label htmlFor="email" className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-18293" data-mp="2auqfp6acjdog">
              Email Address *
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${rt.inputFocus} ${dk.inputOnCard}`} placeholder="john@example.com" required data-id="element-18294" data-mp="zhxr24cur5cm" />
          </div>
          <div className="md:col-span-2" data-id="element-18295" data-mp="2ncukd0y0atei">
            <label htmlFor="company" className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-18296" data-mp="2jdv80v9y3ud1">
              Company/Publisher
            </label>
            <div className="relative" data-id="element-18297" data-mp="29eme22f2yfqo">
              <BriefcaseIcon className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-18298" data-mp="29fo1z7h72wpt" />

              <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange} className={`w-full pl-12 pr-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${rt.inputFocus} ${dk.inputOnCard}`} placeholder="Company Name (Optional)" data-id="element-18299" data-mp="7nssmyiev4ar" />
            </div>
          </div>
        </div>
      </div>

      {/* Project Details */}
      <div data-id="element-18300" data-mp="owu1o0dvimb5">
        <h2 className={`text-xl font-semibold mb-6 flex items-center pb-2 border-b ${dk.textPrimary} ${dk.border}`} data-id="element-18301" data-mp="oxd92n5wdsax">
          <PenIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-18302" data-mp="3odthodrr6n9d" />
          Project Details
        </h2>
        <div className="space-y-6" data-id="element-18303" data-mp="3se3tw86529wo">
          <div data-id="element-18304" data-mp="17spc21x6r0l">
            <label htmlFor="projectType" className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-18305" data-mp="179hxf9wbl0t">
              Project Type *
            </label>
            <select id="projectType" name="projectType" value={formData.projectType} onChange={handleInputChange} className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${rt.inputFocus} ${dk.inputOnCard}`} required data-id="element-18306" data-mp="1cn7nh95v7oxa">
              <option value="" data-id="element-18307" data-mp="1ym5796bbjced">Select a project type</option>
              <option value="Novel" data-id="element-18308" data-mp="2cgo8vfjfubvm">Novel</option>
              <option value="Short Story" data-id="element-18309" data-mp="27owjh8c0qc72">Short Story</option>
              <option value="Series" data-id="element-18310" data-mp="27nd3liu9y35t">Series</option>
              <option value="Anthology" data-id="element-18311" data-mp="2mdkbx5qki5ss">Anthology</option>
              <option value="Non-Fiction Book" data-id="element-18312" data-mp="2mbzi5ic2uqvz">Non-Fiction Book</option>
              <option value="Ghostwriting" data-id="element-18313" data-mp="2hdo9haxiqmfc">Ghostwriting</option>
              <option value="Co-authoring" data-id="element-18314" data-mp="2ve8r43rp6c79">Co-authoring</option>
              <option value="Other" data-id="element-18315" data-mp="2vdpg770gso2q">Other</option>
            </select>
          </div>
          <div data-id="element-18316" data-mp="2qfxejla0pnlc">
            <label htmlFor="projectDescription" className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-18317" data-mp="2qdb8jq2rzg58">
              Project Description *
            </label>
            <textarea id="projectDescription" name="projectDescription" value={formData.projectDescription} onChange={handleInputChange} rows={5} className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${rt.inputFocus} ${dk.inputOnCard}`} placeholder="Please describe your project in detail..." required data-id="element-18318" data-mp="w3r6gvcf7mx9" />
          </div>
        </div>
      </div>

      {/* Timeline and Budget */}
      <div data-id="element-18319" data-mp="2tyd1yqon6hk5">
        <h2 className={`text-xl font-semibold mb-6 flex items-center pb-2 border-b ${dk.textPrimary} ${dk.border}`} data-id="element-18320" data-mp="2ty4mqs51r05s">
          <CalendarIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-18321" data-mp="2fzwvn0uoyt2j" />
          Timeline and Budget
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-18322" data-mp="e67bi6ecoox4">
          <div data-id="element-18323" data-mp="e6yxcpi5b8a7">
            <label htmlFor="timeline" className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-18324" data-mp="e4crcuawl0u3">
              Project Timeline
            </label>
            <select id="timeline" name="timeline" value={formData.timeline} onChange={handleInputChange} className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${rt.inputFocus} ${dk.inputOnCard}`} data-id="element-18325" data-mp="30xrx95yizi3j">
              <option value="" data-id="element-18326" data-mp="3jqm2v9a9dxv3">Select timeline</option>
              <option value="Less than 1 month" data-id="element-18327" data-mp="3jr5keovzyk8l">Less than 1 month</option>
              <option value="1-3 months" data-id="element-18328" data-mp="3ooe5oycfz4e2">1-3 months</option>
              <option value="3-6 months" data-id="element-18329" data-mp="3apo6nd62aun2">3-6 months</option>
              <option value="6-12 months" data-id="element-18330" data-mp="3ap4mdpsxvu5v">6-12 months</option>
              <option value="More than 12 months" data-id="element-18331" data-mp="3eqhvc8rbwdh0">More than 12 months</option>
            </select>
          </div>
          <div data-id="element-18332" data-mp="1z9fzeyfoqv52">
            <label htmlFor="budget" className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-18333" data-mp="1z8xwjiyipio6">
              Budget Range
            </label>
            <select id="budget" name="budget" value={formData.budget} onChange={handleInputChange} className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${rt.inputFocus} ${dk.inputOnCard}`} data-id="element-18334" data-mp="1vra69st52uhr">
              <option value="" data-id="element-18335" data-mp="20sfzdrqpojrt">Select budget range</option>
              <option value="Under $1,000" data-id="element-18336" data-mp="1d1anfnnw6bms">Under $1,000</option>
              <option value="$1,000 - $5,000" data-id="element-18337" data-mp="1czr8c0dz26ml">$1,000 - $5,000</option>
              <option value="$5,000 - $10,000" data-id="element-18338" data-mp="184tyir8vbt8n">$5,000 - $10,000</option>
              <option value="$10,000 - $25,000" data-id="element-18339" data-mp="1msyidyjnqat0">$10,000 - $25,000</option>
              <option value="$25,000+" data-id="element-18340" data-mp="1i1gf33u5vy4v">$25,000+</option>
              <option value="Royalty-based" data-id="element-18341" data-mp="1hzvofpaywzca">Royalty-based</option>
              <option value="Flexible" data-id="element-18342" data-mp="uakizuioagrt">Flexible / To be discussed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Author Preferences */}
      <div data-id="element-18343" data-mp="1f0wt417c176d">
        <h2 className={`text-xl font-semibold mb-6 flex items-center pb-2 border-b ${dk.textPrimary} ${dk.border}`} data-id="element-18344" data-mp="1f16m7xnobuga">
          <BookIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-18345" data-mp="1j0p4elo41rff" />
          Author Preferences
        </h2>
        <div className="space-y-6" data-id="element-18346" data-mp="1ntqn74rfub4a">
          <div data-id="element-18347" data-mp="1ss1tx6mfs5ia">
            <label htmlFor="authorPreferences" className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-18348" data-mp="1ssl2vxu3zjk6">
              Specific Author Requirements
            </label>
            <textarea id="authorPreferences" name="authorPreferences" value={formData.authorPreferences} onChange={handleInputChange} rows={3} className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${rt.inputFocus} ${dk.inputOnCard}`} placeholder="Describe any specific requirements for the author (experience level, writing style, etc.)" data-id="element-18349" data-mp="104fgl6nepmi8" />
          </div>
          <div data-id="element-18350" data-mp="9s2djs1onayk">
            <label className={`block text-sm font-medium mb-3 ${dk.textSecondary}`} data-id="element-18351" data-mp="9slub5fljtb0">
              Genre Interests
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-18352" data-mp="2siq4fgfe6r">
              {genres.map(genre => <button type="button" key={genre} onClick={() => handleGenreChange(genre)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${formData.genreInterests.includes(genre) ? `${rt.bgDark} text-white shadow-md` : dk.badgeNeutral + ' border ' + dk.border}`} data-id="element-18353" data-mp="3raergbh166uh">
                  {genre}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Terms and Submit */}
      <div className={`pt-6 border-t ${dk.border}`} data-id="element-18354" data-mp="1bi7ghafhma2x">
        <div className="flex items-start mb-8" data-id="element-18355" data-mp="16jw7t30xi5ma">
          <div className="flex items-center h-5 mt-0.5" data-id="element-18356" data-mp="16k5x0oe5g8qx">
            <input id="termsAgreed" name="termsAgreed" type="checkbox" checked={formData.termsAgreed} onChange={handleCheckboxChange} className={`h-4 w-4 border-gray-300 rounded cursor-pointer ${rt.checkbox}`} required data-id="element-18357" data-mp="1ejssmwombaa2" />
          </div>
          <div className="ml-3 text-sm" data-id="element-18358" data-mp="19jehh5rpzk7t">
            <label htmlFor="termsAgreed" className={dk.textSecondary} data-id="element-18359" data-mp="vu4a631ydmbl">
              I agree to the{' '}
              <a href="#" className={`font-medium hover:underline ${rt.accentLink}`} data-id="element-18360" data-mp="10l2tlbi8n0j2">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className={`font-medium hover:underline ${rt.accentLink}`} data-id="element-18361" data-mp="qtygyig1brre">
                Privacy Policy
              </a>
            </label>
          </div>
        </div>
        <button type="submit" className={`w-full py-3.5 px-6 rounded-lg font-bold text-lg shadow-lg transform transition-all hover:-translate-y-0.5 text-white ${rt.primaryButton}`} data-id="element-18362" data-mp="gw27rw6nr6vx">
          Submit Request
        </button>
      </div>
    </form>;
  return <ConnectionPageLayout title="Connect with Authors" subtitle="Find co-writers, collaborators, and ghostwriters for your next project" icon={<PenToolIcon className="h-8 w-8" data-id="element-18364" data-mp="h6vtwntseqsr" />} vendorType="authors" filterSections={filterSections} opportunities={opportunities} opportunitiesLink="/authors/collaborations" formContent={formContent} formCTALabel="Submit a Collaboration Request" formCTADescription="Can't find the right author? Submit your project details and we'll match you." data-id="element-18363" data-mp="79r4mf4naf7q" />;
};
export default ConnectWithAuthors;